<script setup lang="ts">
import { computed, ref } from 'vue'

import { Button, Modal, runLoader, Tooltip } from '@collector/shared-ui'
import {
  getParticipantTeamSide,
  SkinPattern,
} from '@collector/shared-ui-domain'
import { CommonStatId, Participant } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import {
  useEvent,
  useParticipantStatsMaps,
  useSport,
} from '@desktop/views/Relation/composables'
import { injectDefaultSkinPatternId } from '@desktop/views/Relation/Sports/provide-inject'

interface Props {
  participant: Participant
}

const props = defineProps<Props>()

type Emits = { close: [] }

const emit = defineEmits<Emits>()

const defaultSkinPatternId = injectDefaultSkinPatternId()

const { homeParticipantStatsMap, awayParticipantStatsMap } =
  useParticipantStatsMaps()
const { event } = useEvent()
const { sport } = useSport()

const participantStatsMap =
  getParticipantTeamSide(props.participant) === 'home'
    ? homeParticipantStatsMap
    : awayParticipantStatsMap

const skins =
  (
    await runLoader(
      sportsApiClient.Skins.getForParticipant(props.participant.id),
    )
  ).api.data.skins || []

const participantSkinId = Number(
  participantStatsMap.value[CommonStatId.SkinPattern]?.value || undefined,
)

const participantSkin = skins.find((skin) => skin.id === participantSkinId)

const participantSkinIndex = participantSkin
  ? skins.indexOf(participantSkin)
  : undefined

const selectedSkinIndex = ref<number | undefined>(participantSkinIndex)

const hasChanges = computed(() => {
  return selectedSkinIndex.value !== participantSkinIndex
})

async function updateParticipantSkin(): Promise<void> {
  await runLoader(
    sportsApiClient.Events.Participants.patch(event.value.id, [
      {
        id: props.participant.id,
        stats: [
          {
            id: CommonStatId.SkinPattern,
            value:
              selectedSkinIndex.value === undefined
                ? ''
                : skins[selectedSkinIndex.value].id,
          },
        ],
      },
    ]),
  )
}

function selectSkin(index: number): void {
  if (selectedSkinIndex.value === index) {
    selectedSkinIndex.value = undefined
  } else {
    selectedSkinIndex.value = index
  }
}
</script>

<template>
  <Modal
    :title="`Select skin for ${participant.short_name}`"
    :confirmButton="{
      text: 'Save',
      confirm: updateParticipantSkin,
      disabled: !hasChanges,
    }"
    @close="emit('close')"
  >
    <div class="flex gap-x-4">
      <Tooltip
        v-for="i in [0, 1, 2]"
        :key="i"
        class="w-full"
      >
        <Button
          class="relative w-full rounded-lg border p-4"
          :class="{
            'bg-neutral-light-0 border-neutral-dark-0': selectedSkinIndex === i,
            'bg-neutral-light-5': selectedSkinIndex !== i,
          }"
          :disabled="!skins[i]"
          @click="selectSkin(i)"
        >
          <Button
            v-if="skins[i] && participantSkinId === skins[i].id"
            class="bg-neutral-dark-10 text-neutral-light-10 absolute -top-3 -left-3 h-8 w-8 rounded-full!"
            icon="check"
            iconSize="lg"
            square
          />
          <SkinPattern
            v-bind="
              skins[i]
                ? {
                    sportName: sport.name,
                    patternId: skins[i].pattern_id,
                    firstColor: `#${skins[i].first_color}`,
                    secondColor: `#${skins[i].second_color}`,
                    thirdColor: `#${skins[i].third_color}`,
                  }
                : {
                    patternId: defaultSkinPatternId,
                    sportName: sport.name,
                    opacity: 0.1,
                  }
            "
          />
        </Button>
        <template
          v-if="!skins[i]"
          #tooltip
        >
          Shirt not available
        </template>
      </Tooltip>
    </div>
  </Modal>
</template>
